import { useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, CreditCard, Plus, RefreshCcw, Search, XCircle, MonitorPlay } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import PageHeader from '../../components/PageHeader';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';
import StatCard from '../../components/StatCard';
import { currency, formatDate, screenLimitLabel, toDate } from '../../utils/format';
import { activateSubscription, cancelSubscription, renewSubscription } from '../../services/subscriptionService';

function stream(name, setter) {
  return onSnapshot(collection(db, name), snapshot => setter(snapshot.docs.map(item => ({ id: item.id, ...item.data() }))));
}

export default function SubscriptionsPage() {
  const { authUser } = useAuth();
  const { notify } = useToast();
  const [searchParams] = useSearchParams();
  const [subscriptions, setSubscriptions] = useState([]);
  const [requests, setRequests] = useState([]);
  const [plans, setPlans] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [search, setSearch] = useState('');
  const [renewing, setRenewing] = useState(null);
  const [renewPlanId, setRenewPlanId] = useState('');
  const [renewPayment, setRenewPayment] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTenantId, setAssignTenantId] = useState('');
  const [assignPlanId, setAssignPlanId] = useState('');
  const [assignPayment, setAssignPayment] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const unsubscribers = [
      stream('subscriptions', setSubscriptions),
      stream('subscription_requests', setRequests),
      stream('plans', setPlans),
      stream('tenants', setTenants)
    ];
    return () => unsubscribers.forEach(unsub => unsub());
  }, []);

  useEffect(() => {
    const tenantId = searchParams.get('tenant');
    if (tenantId) {
      setAssignTenantId(tenantId);
      setAssignOpen(true);
    }
  }, [searchParams]);

  const activePlans = useMemo(() => plans.filter(item => item.status === 'active'), [plans]);
  const pending = useMemo(() => requests.filter(item => item.status === 'pending').sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)), [requests]);
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return subscriptions
      .filter(item => {
        const tenant = tenants.find(company => company.id === item.tenantId);
        return !needle || [item.planName, tenant?.companyName, tenant?.email, item.status].some(value => String(value || '').toLowerCase().includes(needle));
      })
      .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
  }, [subscriptions, tenants, search]);

  const activeCount = subscriptions.filter(item => item.status === 'active' && (!toDate(item.endDate) || toDate(item.endDate) > new Date())).length;
  const expiredCount = subscriptions.filter(item => item.status === 'active' && toDate(item.endDate) && toDate(item.endDate) <= new Date()).length;

  function tenantName(id) {
    return tenants.find(item => item.id === id)?.companyName || id;
  }

  async function approve(request) {
    const plan = plans.find(item => item.id === request.planId);
    if (!plan) { notify('The requested plan no longer exists.', 'error'); return; }
    if (!window.confirm(`Approve ${request.planName} for ${tenantName(request.tenantId)}?`)) return;
    setBusy(true);
    try {
      await activateSubscription({
        tenantId: request.tenantId,
        plan,
        requestId: request.id,
        activatedBy: authUser?.uid || ''
      });
      notify('Subscription approved. Eligible paired TVs are now authorized without re-pairing.', 'success');
    } catch (error) {
      notify(String(error?.message || error), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function reject(request) {
    if (!window.confirm(`Reject ${request.planName} request?`)) return;
    try {
      await updateDoc(doc(db, 'subscription_requests', request.id), {
        status: 'rejected',
        rejectedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      notify('Purchase request rejected.', 'success');
    } catch (error) {
      notify(String(error?.message || error), 'error');
    }
  }

  async function cancel(item) {
    if (!window.confirm(`Cancel ${item.planName || 'subscription'} for ${tenantName(item.tenantId)}? TV playback will be locked.`)) return;
    setBusy(true);
    try {
      await cancelSubscription({ subscriptionId: item.id, tenantId: item.tenantId });
      notify('Subscription cancelled. Paired screens remain paired but playback is locked.', 'success');
    } catch (error) {
      notify(String(error?.message || error), 'error');
    } finally {
      setBusy(false);
    }
  }

  function openRenew(item) {
    setRenewing(item);
    setRenewPlanId(item.planId || '');
    setRenewPayment('');
  }

  async function doRenew() {
    const plan = activePlans.find(item => item.id === renewPlanId);
    if (!plan || !renewing) { notify('Choose a package.', 'error'); return; }
    setBusy(true);
    try {
      await renewSubscription({
        subscriptionId: renewing.id,
        tenantId: renewing.tenantId,
        plan,
        paymentReference: renewPayment.trim(),
        activatedBy: authUser?.uid || ''
      });
      setRenewing(null);
      notify('Subscription renewed/changed. Screen entitlements were synchronized immediately.', 'success');
    } catch (error) {
      notify(String(error?.message || error), 'error');
    } finally {
      setBusy(false);
    }
  }

  async function assignDirect() {
    const plan = activePlans.find(item => item.id === assignPlanId);
    if (!assignTenantId || !plan) { notify('Choose a company and package.', 'error'); return; }
    setBusy(true);
    try {
      await activateSubscription({
        tenantId: assignTenantId,
        plan,
        paymentReference: assignPayment.trim(),
        activatedBy: authUser?.uid || ''
      });
      setAssignOpen(false);
      setAssignPlanId('');
      setAssignPayment('');
      notify('Package activated for the company.', 'success');
    } catch (error) {
      notify(String(error?.message || error), 'error');
    } finally {
      setBusy(false);
    }
  }

  const subscriptionColumns = [
    { key: 'tenant', label: 'Company', render: item => {
      const tenant = tenants.find(company => company.id === item.tenantId);
      return <div><strong>{tenant?.companyName || item.tenantId}</strong><small className="block-muted">{tenant?.email || ''}</small></div>;
    } },
    { key: 'plan', label: 'Package', render: item => <div><strong>{item.planName || '—'}</strong><small className="block-muted">{screenLimitLabel(item.screenLimit)} screens · {Number(item.storageLimitGB || 0)} GB</small></div> },
    { key: 'period', label: 'Period', render: item => <span>{formatDate(item.startDate)} → {formatDate(item.endDate)}</span> },
    { key: 'amount', label: 'Amount', render: item => currency(item.amount) },
    { key: 'status', label: 'Status', render: item => {
      const expired = item.status === 'active' && toDate(item.endDate) && toDate(item.endDate) <= new Date();
      const label = expired ? 'expired' : item.status;
      return <Badge tone={label === 'active' ? 'success' : label === 'cancelled' || label === 'expired' ? 'danger' : 'neutral'}>{label}</Badge>;
    } },
    { key: 'actions', label: 'Actions', render: item => <div className="table-actions"><button className="btn btn-soft btn-sm" onClick={() => openRenew(item)}><RefreshCcw size={14} /> Renew / change</button>{item.status === 'active' && <button className="btn btn-danger btn-sm" onClick={() => cancel(item)}>Cancel</button>}</div> }
  ];

  return (
    <>
      <PageHeader
        eyebrow="Entitlement operations"
        title="Subscriptions"
        description="Approve purchases, assign packages, renew or upgrade companies, and control the exact number of TV screens allowed to play."
        actions={<button className="btn btn-primary" onClick={() => setAssignOpen(true)}><Plus size={17} /> Assign package</button>}
      />

      <div className="stats-grid four">
        <StatCard icon={CreditCard} label="Pending requests" value={pending.length} detail="Awaiting approval" tone="amber" />
        <StatCard icon={CheckCircle2} label="Active" value={activeCount} detail="Playback entitled" tone="green" />
        <StatCard icon={XCircle} label="Expired" value={expiredCount} detail="TV playback locked" tone="purple" />
        <StatCard icon={MonitorPlay} label="History" value={subscriptions.length} detail="All subscription records" tone="blue" />
      </div>

      {pending.length > 0 && <Card>
        <div className="card-head"><div><span className="eyebrow">Requires action</span><h3>Purchase requests</h3></div><Badge tone="warning">{pending.length} pending</Badge></div>
        <DataTable rows={pending} columns={[
          { key: 'tenant', label: 'Company', render: item => tenantName(item.tenantId) },
          { key: 'plan', label: 'Package', render: item => item.planName },
          { key: 'amount', label: 'Amount', render: item => currency(item.amount) },
          { key: 'created', label: 'Requested', render: item => formatDate(item.createdAt) },
          { key: 'actions', label: 'Decision', render: item => <div className="table-actions"><button className="btn btn-success btn-sm" disabled={busy} onClick={() => approve(item)}><CheckCircle2 size={14} /> Approve</button><button className="btn btn-danger btn-sm" onClick={() => reject(item)}><XCircle size={14} /> Reject</button></div> }
        ]} />
      </Card>}

      <Card className="toolbar-card"><div className="search-box"><Search size={17} /><input value={search} onChange={event => setSearch(event.target.value)} placeholder="Search company, package or status…" /></div><div className="toolbar-count">{filtered.length} records</div></Card>
      <Card><DataTable rows={filtered} columns={subscriptionColumns} empty="No subscription records found." /></Card>

      <Modal
        open={assignOpen}
        onClose={() => !busy && setAssignOpen(false)}
        title="Assign / activate package"
        description="Use this when payment has been verified outside the app or when Super Admin provisions a company directly."
        footer={<><button className="btn btn-soft" onClick={() => setAssignOpen(false)} disabled={busy}>Cancel</button><button className="btn btn-primary" onClick={assignDirect} disabled={busy}>{busy ? 'Activating…' : 'Activate package'}</button></>}
      >
        <div className="form-grid">
          <label className="field"><span>Company</span><select value={assignTenantId} onChange={event => setAssignTenantId(event.target.value)}><option value="">Select company…</option>{tenants.filter(item => item.id !== 'super_admin' && item.status !== 'deleted').map(item => <option value={item.id} key={item.id}>{item.companyName || item.email}</option>)}</select></label>
          <label className="field"><span>Package</span><select value={assignPlanId} onChange={event => setAssignPlanId(event.target.value)}><option value="">Select package…</option>{activePlans.map(plan => <option key={plan.id} value={plan.id}>{plan.name} — {screenLimitLabel(plan.screens ?? plan.maxScreens)} screens — {currency(plan.price)}</option>)}</select></label>
          <label className="field"><span>Payment/reference (optional)</span><input value={assignPayment} onChange={event => setAssignPayment(event.target.value)} placeholder="UPI / invoice / transaction reference" /></label>
        </div>
      </Modal>

      <Modal
        open={Boolean(renewing)}
        onClose={() => !busy && setRenewing(null)}
        title="Renew / change package"
        description="The current screen limit is replaced immediately. TVs above the new limit are locked; eligible TVs resume without re-pairing."
        footer={<><button className="btn btn-soft" onClick={() => setRenewing(null)} disabled={busy}>Cancel</button><button className="btn btn-primary" onClick={doRenew} disabled={busy}>{busy ? 'Updating…' : 'Activate renewal'}</button></>}
      >
        <div className="form-grid">
          <label className="field"><span>Package</span><select value={renewPlanId} onChange={event => setRenewPlanId(event.target.value)}><option value="">Select…</option>{activePlans.map(plan => <option key={plan.id} value={plan.id}>{plan.name} — {screenLimitLabel(plan.screens ?? plan.maxScreens)} screens — {currency(plan.price)}</option>)}</select></label>
          <label className="field"><span>Payment/reference (optional)</span><input value={renewPayment} onChange={event => setRenewPayment(event.target.value)} /></label>
        </div>
      </Modal>
    </>
  );
}
